import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSelectedTournament, selectCategory } from '../../../redux/slices/tournamentSlice';
import { AddResultModal } from '../../../components/Fixtures';
import { useFixtureState } from '../../fixtures/hooks';
import useTournamentRole from '../../../hooks/useTournamentRole';
import LockStagesModal from '../../../components/Dashboard/LockStagesModal';

import ResultsViewV2 from "./ResultsViewV2";

/**
 * ResultsV2 - Modern JSX container component with Tailwind CSS
 * Redux-connected container that manages state and API calls
 * Renders ResultsViewV2 presentation component
 */
const ResultsV2 = () => {
  const dispatch = useDispatch();

  // Redux state
  const { selectedOrganisation } = useSelector((state) => state.organisation);
  const { role } = useTournamentRole();

  // Use the shared hook for consistent data logic
  const {
    selectedTournament,

    // Date & View state
    startDate,
    endDate,
    handleDateChange,
    handleShiftDates,

    // Data
    fixturesByDate,
    sortedDates,

    // Modals
    isAddResultModalOpen,
    selectedFixture,
    selectedResult,
    openAddResultModal,
    openEditResultModal,
    closeAddResultModal,
    selectedCategory,
  } = useFixtureState();

  // Build set of locked phase IDs for gating result buttons
  const lockedPhaseIds = useMemo(() => {
    const phases = selectedCategory?.phases || [];
    return new Set(phases.filter((p) => p.locked).map((p) => p._id || p.id));
  }, [selectedCategory]);

  const [isLockStagesModalOpen, setIsLockStagesModalOpen] = useState(false);

  // Fetch tournament data on mount
  useEffect(() => {
    if (!selectedOrganisation?.id) return;
    dispatch(getSelectedTournament(selectedOrganisation.id));
  }, [dispatch, selectedOrganisation?.id]);

  const handleAddResult = (fixture) => {
    openAddResultModal(fixture);
  };

  const handleEditFixture = (fixture) => {
    // For results page, editing often means editing the result
    openEditResultModal(fixture);
  };

  const getMatchTeams = (fixture) => {
    return {
      homeTeam: fixture.homeTeam,
      awayTeam: fixture.awayTeam,
      hasPitchConflict: false, // Hook handles this if needed
      hasEndTimeConflict: false
    };
  };

  return (
    <>
      <ResultsViewV2
        fixturesByDate={fixturesByDate}
        sortedDates={sortedDates}
        selectedTournament={selectedTournament}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange} // Use hook's handler
        onShiftDates={handleShiftDates} // Use hook's handler
        onAddResult={handleAddResult}
        onEditFixture={handleEditFixture}
        getMatchTeams={getMatchTeams}
        selectedCategory={selectedCategory}
        onCategoryChange={(label) => {
          const category = selectedTournament?.categories?.find(c => c.label === label);
          if (category) dispatch(selectCategory(category));
        }}
        lockedPhaseIds={lockedPhaseIds}
        isOwner={role === 'owner'}
        onOpenLockStages={role === 'owner' ? () => setIsLockStagesModalOpen(true) : undefined}
      />

      {isAddResultModalOpen && (
        <AddResultModal
          isOpen={isAddResultModalOpen}
          onClose={closeAddResultModal}
          fixture={selectedFixture}
          result={selectedResult}
          type={selectedResult ? "edit" : "add"}
        />
      )}

      <LockStagesModal
        isOpen={isLockStagesModalOpen}
        onClose={() => setIsLockStagesModalOpen(false)}
      />
    </>
  );
};

export default ResultsV2;
